class VariantImageGalleryContainer extends HTMLElement {
  constructor() {
    super();
    this._boundHandleVariantChange = this._handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.variantGallery = this.querySelector("[data-variant-gallery]");
    document.addEventListener("variant:change", this._boundHandleVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener(
      "variant:change",
      this._boundHandleVariantChange,
    );
  }

  _handleVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    const selectedVariantId = variant.id;
    if (!this.variantGallery) return;

    this.variantGallery.setAttribute(
      "data-current-variant-id",
      selectedVariantId,
    );
    if (typeof this.variantGallery.setActiveVariant === "function") {
      this.variantGallery.setActiveVariant(selectedVariantId);
    }
  }
}

customElements.define(
  "variant-image-gallery-container",
  VariantImageGalleryContainer,
);

const MOBILE_BREAKPOINT = 770;

class VariantImageGallery extends HTMLElement {
  constructor() {
    super();
    this._resizeTimeout = null;
    this._isInitialized = false;
    this._boundHandleResize = this._handleResize.bind(this);
    this._boundPhotoswipeAfterChange = this._onPhotoswipeAfterChange.bind(this);
  }

  connectedCallback() {
    this._cacheElements();
    this._cacheSourceMedia();

    this.initFlickitySlider();
    this.initPhotoSwipeZoom();
    this._isInitialized = true;

    window.addEventListener("resize", this._boundHandleResize);

    if (this._productImagesContainer) {
      this._productImagesContainer.addEventListener(
        "photoswipe:afterChange",
        this._boundPhotoswipeAfterChange,
      );
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._boundHandleResize);
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    if (this._productImagesContainer && this._boundPhotoswipeAfterChange) {
      this._productImagesContainer.removeEventListener(
        "photoswipe:afterChange",
        this._boundPhotoswipeAfterChange,
      );
    }
  }

  _cacheElements() {
    this._productImagesContainer = this.querySelector("[data-product-images]");
    this._mainSlider = this.querySelector("[data-product-photos]");
    this._thumbSlider = this.querySelector("[data-product-thumbs]");
    this._thumbScroller = this.querySelector("[data-product-thumb-scroller]");
  }

  _cacheSourceMedia() {
    this._sourceSlides = Array.from(
      this.querySelectorAll("[data-gallery-source-slide]"),
    ).map((item) => ({
      variantId: item.getAttribute("data-variant-id"),
      isFallback: item.getAttribute("data-variant-fallback") === "true",
      node: item.querySelector(".product-main-slide"),
    }));

    this._sourceThumbs = Array.from(
      this.querySelectorAll("[data-gallery-source-thumb]"),
    ).map((item) => ({
      variantId: item.getAttribute("data-variant-id"),
      isFallback: item.getAttribute("data-variant-fallback") === "true",
      node: item.querySelector(".product__thumb-item"),
    }));
  }

  _destroySlideshow() {
    if (this._slideshow && typeof this._slideshow.destroy === "function") {
      this._slideshow.destroy();
      this._slideshow = null;
    }

    if (this._mainSlider && typeof Flickity !== "undefined") {
      const flkty = Flickity.data(this._mainSlider);
      if (flkty && typeof flkty.destroy === "function") {
        flkty.destroy();
      }
    }
  }

  _buildMediaSet(variantId) {
    let slides = this._sourceSlides.filter(
      (item) => item.node && item.variantId === String(variantId),
    );
    let thumbs = this._sourceThumbs.filter(
      (item) => item.node && item.variantId === String(variantId),
    );

    if (!slides.length) {
      slides = this._sourceSlides.filter(
        (item) => item.node && item.isFallback,
      );
      thumbs = this._sourceThumbs.filter(
        (item) => item.node && item.isFallback,
      );
    }

    return { slides, thumbs };
  }

  _renderMediaSet(variantId) {
    if (!this._mainSlider || !this._thumbScroller) return;

    const mediaSet = this._buildMediaSet(variantId);
    if (!mediaSet.slides.length) return;

    this._destroySlideshow();

    this._mainSlider.innerHTML = "";
    this._thumbScroller.innerHTML = "";

    mediaSet.slides.forEach((item, index) => {
      const slide = item.node.cloneNode(true);
      slide.setAttribute("data-index", index);
      slide.setAttribute("data-variant-id", String(variantId));
      slide.classList.toggle("starting-slide", index === 0);
      slide.classList.toggle("secondary-slide", index !== 0);
      this._mainSlider.appendChild(slide);
    });

    mediaSet.thumbs.forEach((item, index) => {
      const thumbItem = item.node.cloneNode(true);
      thumbItem.setAttribute("data-index", index);
      thumbItem.setAttribute("data-variant-id", String(variantId));
      const thumbLink = thumbItem.querySelector("[data-product-thumb]");
      if (thumbLink) {
        thumbLink.setAttribute("data-index", index);
      }
      this._thumbScroller.appendChild(thumbItem);
    });

    const hasMultipleSlides = mediaSet.slides.length > 1;
    if (this._productImagesContainer) {
      this._productImagesContainer.setAttribute(
        "data-has-slideshow",
        hasMultipleSlides ? "true" : "false",
      );
    }
    if (this._thumbSlider) {
      this._thumbSlider.classList.toggle("medium-up--hide", !hasMultipleSlides);
    }
  }

  _handleResize() {
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => {
      this._resizeTimeout = null;
      this._triggerFlickityResize();
    }, 150);
  }

  _triggerFlickityResize() {
    const mainSlider = this.querySelector("[data-product-photos]");
    if (!mainSlider) return;
    requestAnimationFrame(() => {
      const flkty =
        typeof Flickity !== "undefined" && Flickity.data(mainSlider);
      if (flkty && typeof flkty.resize === "function") {
        flkty.resize();
        return;
      }
      if (this._slideshow && typeof this._slideshow.resize === "function") {
        this._slideshow.resize();
      }
    });
  }

  static get observedAttributes() {
    return ["data-current-variant-id"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === "data-current-variant-id" &&
      newValue &&
      oldValue !== newValue &&
      this._isInitialized &&
      oldValue !== null
    ) {
      this.setActiveVariant(newValue);
    }
  }

  _fadeAndSwap(callback) {
    const slider = this._mainSlider;
    if (!slider) {
      callback();
      return;
    }

    let done = false;
    const proceed = () => {
      if (done) return;
      done = true;
      callback();
      requestAnimationFrame(() => {
        slider.style.opacity = "1";
      });
    };

    slider.style.transition = "opacity 0.12s ease";
    slider.style.opacity = "0";
    slider.addEventListener("transitionend", proceed, { once: true });
    setTimeout(proceed, 180);
  }

  setActiveVariant(variantId) {
    if (!variantId) return;
    this._fadeAndSwap(() => {
      this._renderMediaSet(variantId);
      this.initFlickitySlider();
      this.initPhotoSwipeZoom();
    });
  }

  initPhotoSwipeZoom() {
    const container = this._productImagesContainer;
    if (!container || container.dataset.zoom === "false") return;
    if (typeof theme === "undefined" || !theme.Photoswipe) return;

    const sectionEl = this.closest("[data-section-id]");
    const sectionId =
      (sectionEl && sectionEl.getAttribute("data-section-id")) || "product";
    const variantId = this.getAttribute("data-current-variant-id") || "default";
    const uniqueSectionId = sectionId + "-gallery-" + variantId;

    this._photoswipe = new theme.Photoswipe(container, uniqueSectionId);
  }

  _onPhotoswipeAfterChange(evt) {
    const index = evt.detail && evt.detail.index;
    if (typeof index === "undefined") return;
    const mainSlider = this.querySelector("[data-product-photos]");
    if (!mainSlider) return;
    const flkty = typeof Flickity !== "undefined" && Flickity.data(mainSlider);
    if (flkty && typeof flkty.select === "function") {
      flkty.select(index);
    } else if (
      this._slideshow &&
      typeof this._slideshow.goToSlide === "function"
    ) {
      this._slideshow.goToSlide(index);
    }
  }

  initFlickitySlider() {
    const mainSlider = this._mainSlider;
    const thumbSlider = this._thumbSlider;
    if (!mainSlider) return;

    const slides = mainSlider.querySelectorAll(".product-main-slide");
    const slidesCount = slides.length;

    const runAfterLayout = () => {
      const flkty =
        typeof Flickity !== "undefined" && Flickity.data(mainSlider);
      if (flkty && typeof flkty.resize === "function") {
        flkty.resize();
        return;
      }
      if (this._slideshow && typeof this._slideshow.resize === "function") {
        this._slideshow.resize();
        return;
      }
      if (slidesCount <= 1) {
        const singleSlide = mainSlider.querySelector(".product-main-slide");
        if (singleSlide) singleSlide.classList.add("is-selected");
        return;
      }
      if (typeof theme === "undefined" || !theme.Slideshow) return;

      const thumbScroller =
        thumbSlider && thumbSlider.querySelector(".product__thumbs--scroller");
      const args = {
        dragThreshold: 25,
        adaptiveHeight: true,
        avoidReflow: true,
        initialIndex: 0,
        childNav: thumbSlider || null,
        childNavScroller: thumbScroller || null,
        childVertical: thumbSlider
          ? thumbSlider.dataset.position === "beside"
          : false,
        pageDots: true,
        wrapAround: true,
      };
      this._slideshow = new theme.Slideshow(mainSlider, args);
    };

    requestAnimationFrame(runAfterLayout);
  }
}

customElements.define("variant-image-gallery", VariantImageGallery);
